import { useMemo, useState } from "react";
import axios from "axios";
import { History, ChevronDown, ChevronUp, Filter, X } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Admin-only collapsible audit trail for a booking.
 *
 * Fetches `/admin/bookings/{id}/edit-log` lazily on first open so we don't
 * hammer the API for every card on the list. Each entry is one admin edit
 * — shows the admin's email, the timestamp, and a compact diff of the
 * fields they changed (from → to). Newest edit on top.
 *
 * Once the log grows past ~4 entries a filter bar shows up with two
 * dropdowns:
 *   - Field: e.g. "scheduled_time" — narrows to entries that touched that
 *     field (and only shows that field's diff row).
 *   - Admin: filters to edits made by one admin email.
 * The filter bar is hidden for short logs so a card with 1-2 edits stays
 * uncluttered.
 */
const FILTER_THRESHOLD = 4;

export default function BookingEditLog({ bookingId }) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null); // null = never loaded
  const [loading, setLoading] = useState(false);
  const [fieldFilter, setFieldFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && entries === null) {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/bookings/${bookingId}/edit-log`);
        setEntries(data.entries || []);
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const renderVal = (v) => {
    if (v == null || v === "") return <span className="text-zinc-600">—</span>;
    if (typeof v === "object") return <span className="text-zinc-500">{JSON.stringify(v)}</span>;
    return <span className="text-zinc-300">{String(v)}</span>;
  };

  // Unique fields + admins across the loaded entries — options for the
  // filter dropdowns. Memoised so we don't rebuild on every render.
  const { fieldOptions, adminOptions } = useMemo(() => {
    const fields = new Set();
    const admins = new Set();
    for (const e of entries || []) {
      for (const f of Object.keys(e.diff || {})) fields.add(f);
      const who = e.admin_email || e.admin_id;
      if (who) admins.add(who);
    }
    return {
      fieldOptions: Array.from(fields).sort(),
      adminOptions: Array.from(admins).sort(),
    };
  }, [entries]);

  // Apply filters: entry stays if it touched the selected field AND was
  // made by the selected admin. Empty filter = pass-through.
  const visibleEntries = useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => {
      if (fieldFilter && !(e.diff || {})[fieldFilter]) return false;
      if (adminFilter) {
        const who = e.admin_email || e.admin_id || "";
        if (who !== adminFilter) return false;
      }
      return true;
    });
  }, [entries, fieldFilter, adminFilter]);

  const filtersActive = !!(fieldFilter || adminFilter);
  const showFilterBar = entries && entries.length > FILTER_THRESHOLD;

  return (
    <div className="mt-3 border-t border-white/5 pt-2" data-testid={`edit-log-${bookingId}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300"
        data-testid={`edit-log-toggle-${bookingId}`}
      >
        <span className="flex items-center gap-1.5">
          <History className="w-3.5 h-3.5" />
          Edit history{entries ? ` (${entries.length})` : ""}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="mt-2 space-y-2" data-testid={`edit-log-list-${bookingId}`}>
          {loading && <p className="text-[11px] text-zinc-500">Loading…</p>}
          {!loading && entries && entries.length === 0 && (
            <p className="text-[11px] text-zinc-500">No admin edits yet.</p>
          )}

          {!loading && showFilterBar && (
            <div
              className="flex flex-wrap items-center gap-2 rounded-md bg-zinc-900/60 border border-white/5 px-2 py-1.5"
              data-testid={`edit-log-filters-${bookingId}`}
            >
              <Filter className="w-3 h-3 text-zinc-500" />
              <select
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                className="bg-zinc-800 border border-white/10 text-zinc-200 text-[11px] rounded px-2 py-1 focus:outline-none focus:border-[#0047FF]"
                data-testid={`edit-log-field-filter-${bookingId}`}
                aria-label="Filter by field"
              >
                <option value="">All fields</option>
                {fieldOptions.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <select
                value={adminFilter}
                onChange={(e) => setAdminFilter(e.target.value)}
                className="bg-zinc-800 border border-white/10 text-zinc-200 text-[11px] rounded px-2 py-1 focus:outline-none focus:border-[#0047FF] max-w-[180px]"
                data-testid={`edit-log-admin-filter-${bookingId}`}
                aria-label="Filter by admin"
              >
                <option value="">All admins</option>
                {adminOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              {filtersActive && (
                <button
                  type="button"
                  onClick={() => { setFieldFilter(""); setAdminFilter(""); }}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-zinc-400 hover:text-white ml-auto"
                  data-testid={`edit-log-clear-filters-${bookingId}`}
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
              <span className="text-[10px] text-zinc-500 basis-full">
                Showing {visibleEntries.length} of {entries.length}
              </span>
            </div>
          )}

          {!loading && visibleEntries.length === 0 && entries && entries.length > 0 && filtersActive && (
            <p className="text-[11px] text-zinc-500">No edits match the current filter.</p>
          )}

          {!loading && visibleEntries.map((e) => {
            // When a field filter is active, only render that one row of the
            // diff so the reader isn't distracted by fields they filtered out.
            const diffPairs = Object.entries(e.diff || {}).filter(
              ([field]) => !fieldFilter || field === fieldFilter,
            );
            return (
              <div
                key={e.id}
                className="rounded-md bg-zinc-800/40 border border-white/5 px-3 py-2 text-[11px] space-y-1"
                data-testid={`edit-log-entry-${e.id}`}
              >
                <div className="flex items-center justify-between text-zinc-400">
                  <span className="font-mono text-[10px] truncate max-w-[60%]">{e.admin_email || e.admin_id || "admin"}</span>
                  <span>{fmtTs(e.at)}</span>
                </div>
                <div className="space-y-0.5">
                  {diffPairs.map(([field, { from, to }]) => (
                    <div key={field} className="flex flex-wrap gap-1 text-[11px]">
                      <span className="font-bold text-[#7AADFF] whitespace-nowrap">{field}:</span>
                      {renderVal(from)}
                      <span className="text-zinc-600">→</span>
                      {renderVal(to)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
